import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSubprojectsApi } from '../services/subprojects';
import { useAuth } from '../hook_au/useAuth';
import {
  type SubprojectDetail,
  type Room,
  type RoomItem,
  type Uuid,
} from '../types/subprojects';

interface SubprojectTotals {
  roomsCount: number;
  itemsCount: number;
  plannedBudget: number;
  actualCost: number;
  actualBudget: number;
  plannedMargin: number;
  realMargin: number;
  totalRoomPlanned: number;
  totalRoomReal: number;
  itemCost: number;
  itemPvp: number;
  itemDiscount: number;
  itemBenefit: number;
  itemMounting: number;
  marginSpread: number;
}

interface UseSubprojectDetailData {
  subproject: SubprojectDetail;
  rooms: Room[];
  totals: SubprojectTotals;
}

interface UseSubprojectDetailResult {
  data: UseSubprojectDetailData | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const toNumber = (value: string | null | undefined) => {
  const numeric = value ? Number.parseFloat(value) : 0;
  return Number.isFinite(numeric) ? numeric : 0;
};

const clampDiscount = (value: number) => Math.min(Math.max(value, 0), 100);

export function useSubprojectDetail(
  projectId: Uuid | null,
  subprojectId: Uuid | null,
): UseSubprojectDetailResult {
  const { token } = useAuth();
  const { getSubproject } = useSubprojectsApi();
  const [data, setData] = useState<UseSubprojectDetailData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const netPvpForItem = (item: RoomItem) => {
  const base = toNumber(item.pvp);
  const discount = clampDiscount(toNumber(item.discount)) / 100;
  return base * (1 - discount);
};
  const sumRoomPlanned = (room: Room) =>
    room.items.reduce((total, item) => total + netPvpForItem(item) * item.quantity, 0);

  const sumRoomReal = (room: Room) =>
  room.items.reduce((total, item) => {
    const lineCost = toNumber(item.real_cost_modi ? item.real_cost : item.unit_cost) *
      (item.real_cost_modi ? 1 : item.quantity);
    return total + lineCost + toNumber(item.mounting);
  }, 0);

  const buildTotals = useCallback((detail: SubprojectDetail): SubprojectTotals => {
    const base = detail.rooms.reduce(
      (acc, room) => {
        acc.roomsCount += 1;
        acc.totalRoomPlanned += sumRoomPlanned(room);
        acc.totalRoomReal += sumRoomReal(room);
        room.items.forEach(item => {
          const cost = item.real_cost_modi
            ? toNumber(item.real_cost)
            : toNumber(item.unit_cost) * item.quantity;

          acc.itemsCount += 1;
          acc.itemCost += cost;
          acc.itemPvp += netPvpForItem(item) * item.quantity;
          acc.itemDiscount += toNumber(item.discount);
          acc.itemMounting += toNumber(item.mounting);
        });

        return acc;
      },
      {
        roomsCount: 0,
        itemsCount: 0,
        totalRoomPlanned: 0,
        totalRoomReal: 0,
        itemCost: 0,
        itemPvp: 0,
        itemDiscount: 0,
        itemBenefit: 0,
        itemMounting: 0,
      },
    );

    const plannedBudget = toNumber(detail.planned_budget);
    const actualCost = toNumber(detail.actual_cost);
    const actualBudget = toNumber(detail.actual_budget);
    const plannedMargin = toNumber(detail.planned_margin);
    const realMargin = toNumber(detail.real_margin);

    return {
      roomsCount: base.roomsCount,
      itemsCount: base.itemsCount,
      plannedBudget,
      actualCost,
      actualBudget,
      plannedMargin,
      realMargin,
      totalRoomPlanned: base.totalRoomPlanned,
      totalRoomReal: base.totalRoomReal,
      itemCost: base.itemCost,
      itemPvp: base.itemPvp,
      itemDiscount: base.itemDiscount,
      itemBenefit: base.itemBenefit,
      itemMounting: base.itemMounting,
      marginSpread: realMargin - plannedMargin,
    };
  }, []);

  const shouldFetch = Boolean(token && projectId && subprojectId);

  const loadDetail = useCallback(
    async (signal?: AbortSignal) => {
      if (!shouldFetch) {
        setData(null);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const detail = await getSubproject(subprojectId as Uuid);
        if (signal?.aborted) return;

        if (detail.project_id !== projectId) {
          throw new Error('El subproyecto no pertenece al proyecto solicitado');
        }

        const totals = buildTotals(detail);
        setData({ subproject: detail, rooms: detail.rooms, totals });
      } catch (err) {
        if (signal?.aborted) return;
        setError(err instanceof Error ? err : new Error('Error desconocido al cargar el subproyecto'));
      } finally {
        if (!signal?.aborted) {
          setIsLoading(false);
        }
      }
    },
    [shouldFetch, getSubproject, subprojectId, projectId, buildTotals],
  );

  useEffect(() => {
    const controller = new AbortController();
    void loadDetail(controller.signal);
    return () => controller.abort();
  }, [loadDetail]);

  const refetch = useCallback(async () => {
    const controller = new AbortController();
    await loadDetail(controller.signal);
  }, [loadDetail]);

  return useMemo(
    () => ({
      data,
      isLoading,
      error,
      refetch,
    }),
    [data, isLoading, error, refetch],
  );
}
